import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/group/members/candidates';

const GroupCandidateSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
});

const GroupCandidatesResponseSchema = z.object({ candidates: z.array(GroupCandidateSchema) });

export type GroupCandidate = z.infer<typeof GroupCandidateSchema>;
export type GroupCandidates = GroupCandidate[];

export async function loadGroupCandidatesQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = GroupCandidatesResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'groupCandidates');
    throw new SilentError();
  }

  return parseResult.data.candidates;
}

const noGroupCandidates: GroupCandidates = [];

export function useLoadGroupCandidates(options: QueryOptions<GroupCandidates> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<GroupCandidates, ApiError>({
    queryKey: ['groupCandidates'],
    queryFn: () => loadGroupCandidatesQueryFn(axiosInstance),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return noGroupCandidates;
    },
    ...options,
  });

  return addResourceNameToQueryResult<
    GroupCandidates,
    ApiError,
    'groupCandidates',
    'PreventUndefined'
  >('groupCandidates', queryResult, noGroupCandidates);
}
